import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../components/Form';
import { formatParam } from '../../../Control/Content/helpers';
import { ControlItem } from '../../../Control/types';

import ServicePaths from '../../../../bridge/utils/ServicePaths';

interface Props {
  botId: string;
}

const MrcStateForm = ({ botId }: Props) => {
  const [direction, setDirection] = useState<string | undefined>();
  const [state, setState] = useState<string | undefined>();
  const [orderType, setOrderType] = useState<string | undefined>();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const checkValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof direction === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        direction: Validations.Required,
      }));

      didError = true;
    } else if (direction !== 'up' && direction !== 'down') {
      setErrors((prev) => ({
        ...prev,
        direction: Validations.DirectionType,
      }));

      didError = true;
    }

    if (typeof state === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        state: Validations.Required,
      }));

      didError = true;
    } else if (
      state !== 'PENDING' &&
      state !== 'RESET' &&
      state !== 'INITIALISED'
    ) {
      setErrors((prev) => ({
        ...prev,
        state: Validations.MrcStateType,
      }));

      didError = true;
    }

    if (typeof orderType === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        orderType: Validations.Required,
      }));

      didError = true;
    } else if (orderType !== 'BUY' && orderType !== 'SELL') {
      setErrors((prev) => ({
        ...prev,
        orderType: Validations.OrderType,
      }));

      didError = true;
    }

    return !didError;
  }, [direction, orderType, state]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.SetMrcState,
      params: {
        botId: formatParam(botId),
        direction: formatParam(direction),
        state: formatParam(state),
        orderType: formatParam(orderType),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [botId, checkValidation, direction, orderType, state]);

  const config = useMemo(() => {
    return {
      title: ControlItem.MrcState,
      formMessage,
      fields: [
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: () => {
            /** noop */
          },
          error: errors.botId,
          isLocked: true,
        },
        {
          label: 'Direction',
          value: direction,
          handleValueChange: setDirection,
          error: errors.direction,
        },
        {
          label: 'State',
          value: state,
          handleValueChange: setState,
          error: errors.state,
        },
        {
          label: 'Order Type',
          value: orderType,
          handleValueChange: setOrderType,
          error: errors.orderType,
        },
      ],
      handleSubmit,
      isLoading,
    };
  }, [
    botId,
    direction,
    errors.botId,
    errors.direction,
    errors.orderType,
    errors.state,
    formMessage,
    handleSubmit,
    isLoading,
    orderType,
    state,
  ]);

  return <Form config={config} />;
};

export default MrcStateForm;
